import { useEffect, useMemo, useState } from "react";
import { Cascader, Input } from "antd";
import { getFieldOptions } from "../../apiservice/workflow";

/**
 * Cascading Module -> (related module) -> Field picker for workflow
 * condition dot-paths ("<module>.<field>", or
 * "<module>.<relatedModule>.<field>" for a one-hop related-table field —
 * see backend/src/workflow/relations.js). Renders as an AntD Cascader: a
 * single control that opens a click-through flyout — pick the module
 * first, then either one of its own fields or a related module's fields
 * one level deeper, matching this project's SOAR-style playbook
 * reference. The displayed value reads as a breadcrumb, e.g.
 * "Purchase Order / Vendor / Credit Limit".
 *
 * `relatedModules` is only non-empty for modules with a declared relation
 * (currently sales_order/purchase_order -> accounts) — everything else
 * just shows the module's own fields as the second (leaf) column.
 *
 * IMPORTANT: the parent only ever fetches/passes field metadata for the
 * *anchor* module (fieldsByModule[anchorModule.key]) — it has no reason to
 * also fetch every related module's own fields up front. So a related
 * module node is marked `isLeaf: false` with no `children` yet, and this
 * component lazily fetches that module's fields itself (via Cascader's
 * `loadData`, reusing the same GET /workflow/field-options endpoint) the
 * first time the user actually expands it, caching the result locally.
 * This is what makes it show a real expand affordance instead of looking
 * like a flat, indistinguishable leaf.
 */
export default function FieldPathPicker({
  value,
  onChange,
  anchorModule, // { key, label }
  relatedModules = [], // [{ key, label }]
  fieldsByModule = {}, // { [moduleKey]: [{ name, label, type }] } — anchor module only, guaranteed
  placeholder = "Select a field",
}) {
  const [lazyFields, setLazyFields] = useState({}); // { [relatedModuleKey]: [{name,label,type}] }
  const [options, setOptions] = useState([]);

  const toFieldLeaves = (fields) =>
    (fields || []).map((f) => ({ value: f.name, label: f.label || f.name, isLeaf: true }));

  // Rebuild the tree whenever the anchor module, its known fields, or the
  // set of related modules changes. Related-module nodes are explicitly
  // isLeaf:false with no `children` key — Cascader treats that combination
  // as "expandable, not yet loaded" and calls `loadData` on click.
  useEffect(() => {
    if (!anchorModule?.key) {
      setOptions([]);
      return;
    }
    const relatedNodes = relatedModules
      .filter((m) => m && m.key)
      .map((m) => {
        const fetched = lazyFields[m.key];
        return fetched
          ? { value: m.key, label: m.label || m.key, children: toFieldLeaves(fetched) }
          : { value: m.key, label: m.label || m.key, isLeaf: false };
      });
    setOptions([
      {
        value: anchorModule.key,
        label: anchorModule.label || anchorModule.key,
        children: [...toFieldLeaves(fieldsByModule[anchorModule.key]), ...relatedNodes],
      },
    ]);
  }, [anchorModule?.key, anchorModule?.label, relatedModules, fieldsByModule, lazyFields]);

  const loadData = async (selectedOptions) => {
    const target = selectedOptions[selectedOptions.length - 1];
    if (lazyFields[target.value]) return; // already fetched — Cascader shouldn't call this, but be safe
    target.loading = true;
    setOptions((prev) => [...prev]);
    let fields = [];
    try {
      const res = await getFieldOptions(target.value);
      const data = res?.data?.data || res?.data || {};
      fields = data.fields || [];
    } catch {
      fields = [];
    }
    target.loading = false;
    setLazyFields((prev) => ({ ...prev, [target.value]: fields }));
  };

  const cascaderValue = useMemo(() => {
    const parts = String(value || "").split(".").filter(Boolean);
    return parts.length >= 2 ? parts : undefined;
  }, [value]);

  const hasSelectableFields = (options[0]?.children || []).length > 0;

  // Nothing to browse yet (module not resolved, or it has no field
  // metadata and no related modules) — fall back to free text rather
  // than an empty flyout.
  if (!hasSelectableFields) {
    return (
      <Input
        value={value || ""}
        placeholder="e.g. purchase_order.total_amount"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <Cascader
      style={{ width: "100%" }}
      options={options}
      value={cascaderValue}
      onChange={(path) => onChange(path ? path.join(".") : "")}
      loadData={loadData}
      showSearch
      allowClear
      placeholder={placeholder}
    />
  );
}

/**
 * Looks up the declared type ("number"|"date"|"text") for a dot-path's
 * field. The field lives on the second-to-last segment's table (the
 * anchor module for a 2-segment path, the related module for a 3-segment
 * one) — not always `parts[0]`.
 *
 * Note: `fieldsByModule` as passed down from WorkflowGraphEditor only ever
 * carries the anchor module's own fields (see FieldPathPicker's lazy
 * loadData above for why) — so for a 3-segment related-module path this
 * still falls back to "text" today, since the caller has no visibility
 * into what this component fetched lazily for its own Cascader. Fixed the
 * indexing here regardless so this is correct as soon as a caller passes
 * richer data; not a behavior change for today's 2-segment paths.
 */
export function fieldTypeForPath(fieldsByModule, path) {
  return fieldDefForPath(fieldsByModule, path)?.type || "text";
}

// Same lookup as fieldTypeForPath, but returns the whole field definition
// (e.g. a "lookup" field's optionsSource) instead of just its type string —
// needed by callers that render a picker from that metadata, not just a
// typed input.
export function fieldDefForPath(fieldsByModule, path) {
  const parts = String(path || "").split(".").filter(Boolean);
  if (parts.length < 2) return null;
  const moduleKey = parts[parts.length - 2];
  const fieldName = parts[parts.length - 1];
  return (fieldsByModule[moduleKey] || []).find((f) => f.name === fieldName) || null;
}
